import React, { useEffect, useState } from "react";

import { API_ENDPOINT } from "../../../constants/appConstant";
import { useNetwork } from "../../../hooks/useNetwork";
import {
  ProfileAPIDataType,
  VerificationApopProps,
  VerificationStepsType,
} from "../../../types";
import { getSiteProps } from "../../../utils/siteProps";
import { ChooseVerificationSkeletonLoader } from "../verification/ChooseVerification/ChooseVerificationSkeletonLoader";
import { ChangeNumber } from "./ChangeNumber";
import { ChooseVerification } from "./ChooseVerification";
import { EmailOTP } from "./EmailOTP";
import { EmailOTPSkeletonLoader } from "./EmailOTP/EmailOTPSkeletonLoader";
import { MobileOTP } from "./MobileOTP";
import { MobileOTPSkeletonLoader } from "./MobileOTP/MobileOTPSkeletonLoader";

export const VerificationApop = ({
  type,
  prefilledData,
  nextApopType,
  onCTA,
  showEmailVerification,
  isRecaptchaEnabled,
  toast,
  closeApop,
  flowType,
  jobData,
  handleEventTracking,
}: VerificationApopProps) => {
  const siteConfig = getSiteProps(window.location.host);
  const [step, setStep] = useState<VerificationStepsType>(type);
  // purly useing for traking purpose
  const [verifyType, setVerifyType] = useState<"" | "ADD" | "UPDATE">("");

  const updateStepHandler = (newStep: VerificationStepsType) => {
    setStep(newStep);
    if (newStep === "ADD_NUMBER") {
      setVerifyType("ADD");
    }

    if (newStep === "CHANGE_NUMBER") {
      setVerifyType("UPDATE");
    }
  };

  const { response: profileAPIData, fetchData: fetchProfileData } =
    useNetwork<ProfileAPIDataType>();

  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [otpId, setOtpId] = useState("");

  const updateOTPIdHandler = (otpId: string) => {
    setOtpId(otpId);
  };

  const updateMobileNumberAndCountryCodeHandler = (
    newCountryCode: string,
    newMobileNumber: string,
  ) => {
    setCountryCode((prev) => (prev !== newCountryCode ? newCountryCode : prev));
    setMobileNumber((prev) =>
      prev !== newMobileNumber ? newMobileNumber : prev,
    );
  };

  const validateProfileHandler = async () => {
    const isRexMonster = siteConfig?.site_map === "rexmonster";

    const profileData = await fetchProfileData(
      API_ENDPOINT().seekerProfile.validateProfileMiddleware,
      "GET",
    );

    const userVerificationPrefillData = (profileData.data as any)
      ?.userVerificationPrefill;

    const email = userVerificationPrefillData?.email ?? "";
    const mobileNumber =
      userVerificationPrefillData?.mobileNumber?.mobileNumber ?? "";
    const countryCode =
      userVerificationPrefillData?.mobileNumber?.countryCode ?? "";

    setCountryCode(countryCode);
    setMobileNumber(mobileNumber);
    setEmail(email);

    if (!isRexMonster) {
      if (!userVerificationPrefillData.isEmailVerified) {
        setStep("EMAIL_OTP_SKELETON");
        return;
      }

      if (!mobileNumber) {
        setStep("ADD_NUMBER");
        setVerifyType("ADD");
        return;
      }
    }
  };

  useEffect(() => {
    if (flowType === "L1_APOP") {
      // Calling validate profile API to get the latest data in case of non job flow
      validateProfileHandler();
    } else {
      if (prefilledData && prefilledData?.userVerificationPrefill) {
        const prefillEmail =
          (prefilledData?.userVerificationPrefill as any)?.email ?? "";
        const prefillMobileNumber =
          (prefilledData?.userVerificationPrefill as any)?.mobileNumber
            ?.mobileNumber ?? "";
        const prefillCountryCode =
          (prefilledData?.userVerificationPrefill as any)?.mobileNumber
            ?.countryCode ?? "";
        setMobileNumber(prefillMobileNumber);
        setCountryCode(prefillCountryCode);
        setEmail(prefillEmail);

        if (prefillMobileNumber) {
          // setStep("MOBILE_OTP");
          setStep("MOBILE_OTP_SKELETON");
        } else {
          setStep("ADD_NUMBER");
          setVerifyType("ADD");
        }
      }
    }
  }, [flowType]);

  let renderedComponent;

  switch (step) {
    case "VERIFICATION":
      renderedComponent = (
        <ChooseVerification
          updateStepHandler={updateStepHandler}
          countryCode={countryCode}
          mobileNumber={mobileNumber}
          email={email}
          showEmailVerification={showEmailVerification}
          isEmailVerified={
            (prefilledData?.userVerificationPrefill as any)?.isEmailVerified ??
            false
          }
          isMobileVerified={
            (prefilledData?.userVerificationPrefill as any)?.isMobileVerified ??
            false
          }
          isRecaptchaEnabled={isRecaptchaEnabled}
          toast={toast}
          updateOTPIdHandler={updateOTPIdHandler}
          onCTA={onCTA}
          closeApop={closeApop}
        />
      );
      break;

    case "MOBILE_OTP_SKELETON":
      renderedComponent = (
        <MobileOTPSkeletonLoader
          updateStepHandler={updateStepHandler}
          countryCode={countryCode}
          mobileNumber={mobileNumber}
          toast={toast}
          onCTA={onCTA}
          closeApop={closeApop}
          updateOTPIdHandler={updateOTPIdHandler}
          isRecaptchaEnabled={isRecaptchaEnabled}
        />
      );
      break;

    case "MOBILE_OTP":
      renderedComponent = (
        <MobileOTP
          updateStepHandler={updateStepHandler}
          countryCode={countryCode}
          mobileNumber={mobileNumber}
          toast={toast}
          otpId={otpId}
          nextApopType={nextApopType}
          onCTA={onCTA}
          closeApop={closeApop}
          isRecaptchaEnabled={isRecaptchaEnabled}
          flowType={flowType}
          jobData={jobData}
          verifyType={verifyType}
          handleEventTracking={handleEventTracking}
        />
      );
      break;

    case "EMAIL_OTP":
      renderedComponent = (
        <EmailOTP
          email={email}
          toast={toast}
          countryCode={countryCode}
          mobileNumber={mobileNumber}
          updateStepHandler={updateStepHandler}
          otpId={otpId}
          nextApopType={nextApopType}
          closeApop={closeApop}
        />
      );
      break;

    case "EMAIL_OTP_SKELETON":
      renderedComponent = (
        <EmailOTPSkeletonLoader
          updateStepHandler={updateStepHandler}
          email={email}
          toast={toast}
          onCTA={onCTA}
          closeApop={closeApop}
          updateOTPIdHandler={updateOTPIdHandler}
          isRecaptchaEnabled={isRecaptchaEnabled}
        />
      );
      break;

    case "CHANGE_NUMBER":
      renderedComponent = (
        <ChangeNumber
          updateStepHandler={updateStepHandler}
          countryCode={countryCode}
          mobileNumber={mobileNumber}
          isRecaptchaEnabled={isRecaptchaEnabled}
          updateOTPIdHandler={updateOTPIdHandler}
          updateMobileNumberAndCountryCodeHandler={
            updateMobileNumberAndCountryCodeHandler
          }
          toast={toast}
          onCTA={onCTA}
          closeApop={closeApop}
          jobData={jobData}
          nextApopType={nextApopType}
          flowType={flowType}
        />
      );
      break;

    case "ADD_NUMBER":
      renderedComponent = (
        <ChangeNumber
          updateStepHandler={updateStepHandler}
          countryCode={countryCode}
          isAddNumber={true}
          mobileNumber={mobileNumber}
          isRecaptchaEnabled={isRecaptchaEnabled}
          updateOTPIdHandler={updateOTPIdHandler}
          updateMobileNumberAndCountryCodeHandler={
            updateMobileNumberAndCountryCodeHandler
          }
          toast={toast}
          onCTA={onCTA}
          closeApop={closeApop}
          jobData={jobData}
          nextApopType={nextApopType}
          flowType={flowType}
        />
      );
      break;

    default:
      renderedComponent = (
        <ChooseVerification
          updateStepHandler={updateStepHandler}
          countryCode={countryCode}
          mobileNumber={mobileNumber}
          showEmailVerification={showEmailVerification}
          email={email}
          isEmailVerified={
            (prefilledData?.userVerificationPrefill as any)?.isEmailVerified ??
            false
          }
          isMobileVerified={
            (prefilledData?.userVerificationPrefill as any)?.isMobileVerified ??
            false
          }
          toast={toast}
          updateOTPIdHandler={updateOTPIdHandler}
          onCTA={onCTA}
          closeApop={closeApop}
        />
      );
      break;
  }

  return (
    <>
      {profileAPIData && profileAPIData.isLoading ? (
        <ChooseVerificationSkeletonLoader />
      ) : (
        <>{renderedComponent}</>
      )}
    </>
  );
};
